import {
  BodySiteCode,
  PathologyId,
  LocaleCode,
  CompanyId,
  Condition as ConditionBase,
  ConditionId,
  UserId,
  BodySite,
  Pathology,
  AssigmentSource,
  Perspective,
  ConditionClinicalTrial,
  BaseUser
} from '@legit-health/ui';
import PatientConditionNotFound from '@/shared/classes/PatientConditionNotFound';
import PrivateInsuranceProvider from '@/shared/interfaces/PrivateInsuranceProvider';
import isAlopecia from '@/shared/utils/isAlopecia';
import Practitioner from './Practitioner';

export interface PatientTeaser extends BaseUser {
  isReferred: boolean;
  nhc: string | null;
  isEnabled: boolean;
  lastLogin: string;
  idCard: string | null;
  birthDate: string | null;
  telephone: string | null;
  studyTerminatedAt: string | null;
  studyTerminatedBy: string | null;
}

export class Condition extends ConditionBase {
  constructor(
    readonly id: ConditionId,
    readonly bodySite: BodySite,
    readonly pathology: Pathology,
    readonly practitioner: UserId,
    readonly frequency: number,
    readonly isTracked: boolean,
    readonly canBeTracked: boolean,
    readonly assigmentSource: AssigmentSource,
    readonly firstImage: string | null,
    readonly firstImageThumbnail: string | null,
    readonly createdAt: string,
    readonly firstImageOfPerspective: Record<Perspective, string>,
    readonly hasPhotoRequested: boolean,
    readonly clinicalTrial: ConditionClinicalTrial
  ) {
    super(
      id,
      bodySite,
      pathology,
      practitioner,
      frequency,
      isTracked,
      assigmentSource,
      firstImage,
      firstImageThumbnail,
      createdAt,
      firstImageOfPerspective,
      canBeTracked
    );
  }

  isAlopecia() {
    return isAlopecia(this.pathology);
  }
}

export default class Patient implements BaseUser {
  constructor(
    readonly id: UserId,
    readonly companyId: CompanyId,
    readonly name: string,
    readonly surname: string,
    readonly secondSurname: string | null,
    readonly gender: string | null,
    readonly locale: LocaleCode,
    readonly fullname: string,
    readonly country: string | null,
    readonly email: string | null,
    readonly image: string | null,
    readonly imageThumbnail: string | null,
    readonly isEnabled: boolean,
    readonly isDeleted: boolean,
    readonly birthDate: string | null,
    readonly telephone: string | null,
    readonly isReferred: boolean,
    readonly emergencyContact: string | null,
    readonly address: string | null,
    readonly languages: string | null,
    readonly careGaps: string | null,
    readonly socialDeterminants: string | null,
    readonly skinType: string | null,
    readonly riskFactors: string | null,
    readonly allergies: string | null,
    readonly weight: string | null,
    readonly height: string | null,
    readonly nhc: string | null,
    readonly idCard: string | null,
    readonly healthCenter: string | null,
    readonly healthCardNumber: string | null,
    readonly privateInsuranceProvider: PrivateInsuranceProvider,
    readonly habits: string | null,
    readonly conditions: Condition[],
    readonly practitioner: Practitioner,
    readonly referredToPractitioners: Practitioner[],
    readonly isAllowedToUploadPhotos: boolean,
    readonly assignedPathologies: PathologyId[],
    readonly studyTerminatedAt: string | null,
    readonly studyTerminatedBy: string | null
  ) {}

  getAnalyzableConditions() {
    return this.conditions.filter((c) => c.canBeTracked);
  }

  findCondition(this: Patient, conditionId: ConditionId) {
    return this.conditions.find((condition) => condition.id === conditionId);
  }

  getCondition(this: Patient, conditionId: ConditionId) {
    const condition = this.findCondition(conditionId);
    if (!condition) {
      throw new PatientConditionNotFound(
        `Condition with id <${conditionId}> not found`,
        conditionId
      );
    }
    return condition;
  }

  findConditionByPathologyAndBodySite(
    this: Patient,
    pathologyId: PathologyId,
    bodySiteCode: BodySiteCode
  ) {
    return (
      this.conditions.find(
        ({bodySite, pathology}) => bodySite.code === bodySiteCode && pathology.id === pathologyId
      ) ?? null
    );
  }
}
